import { useEffect } from "react";

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  /** When true, adds <meta name="robots" content="noindex, nofollow"> for admin/auth surfaces */
  noindex?: boolean;
}

const BASE_URL = "https://nardyautomation.com";

export const useSEO = ({ title, description, canonical, ogImage, noindex }: SEOProps) => {
  useEffect(() => {
    document.title = title;

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", description);

    const canonicalEl = document.querySelector('link[rel="canonical"]');
    const canonicalUrl = canonical ? `${BASE_URL}${canonical}` : undefined;
    if (canonicalEl && canonicalUrl) {
      canonicalEl.setAttribute("href", canonicalUrl);
    }

    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute("content", title);

    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute("content", description);

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl && canonicalUrl) ogUrl.setAttribute("content", canonicalUrl);

    // Set og:image to product image when available
    const ogImageEl = document.querySelector('meta[property="og:image"]');
    const twitterImageEl = document.querySelector('meta[name="twitter:image"]');
    if (ogImage) {
      if (ogImageEl) ogImageEl.setAttribute("content", ogImage);
      if (twitterImageEl) twitterImageEl.setAttribute("content", ogImage);
    }

    // Set og:type to product for product pages
    const ogType = document.querySelector('meta[property="og:type"]');
    if (ogType && ogImage) {
      ogType.setAttribute("content", "product");
    }

    // robots meta — added dynamically only for noindex routes
    let robotsEl = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (noindex) {
      if (!robotsEl) {
        robotsEl = document.createElement("meta");
        robotsEl.setAttribute("name", "robots");
        document.head.appendChild(robotsEl);
      }
      robotsEl.setAttribute("content", "noindex, nofollow");
    } else if (robotsEl) {
      robotsEl.setAttribute("content", "index, follow");
    }

    return () => {
      // Reset to defaults when leaving the page
      if (ogType) ogType.setAttribute("content", "website");
      // Reset robots to indexable so admin/auth noindex doesn't leak into public pages
      const r = document.querySelector('meta[name="robots"]');
      if (r) r.setAttribute("content", "index, follow");
    };
  }, [title, description, canonical, ogImage, noindex]);
};
